import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ExternalLink,
  Globe,
  BookOpen,
  ArrowRight,
  Newspaper,
  Building2,
  GraduationCap,
  Network,
  FileText,
  Landmark,
  AlertCircle
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import StickyBottomNav from "../../../components/Navbar/StickyBottomNav";

// Helper function to choose icon based on organization type
const getOrganizationIcon = (organization) => {
  if (
    organization.includes("UNHCR") ||
    organization.includes("Holocaust Museum")
  ) {
    return Building2;
  }
  if (
    organization.includes("CNN") ||
    organization.includes("Washington Post") ||
    organization.includes("The Conversation")
  ) {
    return Newspaper;
  }
  if (organization.includes("Origins") || organization.includes("Brookings")) {
    return GraduationCap;
  }
  if (organization === "Holocaust Museum") {
    return Landmark;
  }
  return FileText;
};

const ResourceCard = ({ organization, links, onExpand, isExpanded }) => {
  const Icon = getOrganizationIcon(organization);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
      onClick={onExpand}
      className="w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-display text-h3 text-secondary">
              {organization}
            </h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="w-8 h-8 rounded-full bg-lightTeal flex items-center justify-center"
          >
            <ArrowRight className="w-4 h-4 text-primary" />
          </motion.div>
        </div>

        <motion.div
          initial={false}
          animate={{
            height: isExpanded ? "auto" : 0,
            opacity: isExpanded ? 1 : 0,
            marginTop: isExpanded ? 16 : 0
          }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 pl-14">
            {links.map((link, index) => (
              <motion.div
                key={index}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 group"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-6 h-6 rounded-full bg-lightTeal flex items-center justify-center">
                  <ExternalLink className="w-3 h-3 text-primary group-hover:text-secondary transition-colors duration-200" />
                </div>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-textPrimary hover:text-primary text-bodyRegular transition-colors duration-200 break-all"
                >
                  {link}
                </a>
              </motion.div>
            ))}
          </div>
          <div className="w-16 h-1 bg-cta mt-6 ml-14"></div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Quote = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    className="relative w-full max-w-4xl mx-auto px-6 py-12"
  >
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <div className="absolute -top-20 -left-20 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-primary opacity-5" />
      <div className="absolute bottom-1/4 -right-20 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-cta opacity-5" />
    </div>

    <div className="relative bg-white rounded-xl shadow-md p-8 md:p-12">
      <div className="w-16 h-16 rounded-full bg-lightTeal flex items-center justify-center mb-6 mx-auto">
        <BookOpen className="w-8 h-8 text-primary" />
      </div>
      <blockquote className="text-2xl md:text-3xl font-display text-secondary mb-4 text-center">
        "Education is the most powerful weapon which you can use to change the
        world."
      </blockquote>
      <cite className="block text-textPrimary text-center text-lg">— Nelson Mandela</cite>
      <div className="w-16 h-1 bg-cta mx-auto mt-6"></div>
    </div>
  </motion.div>
);

const MigrationPolicy = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const resources = [
    {
      organization: "Brookings.Edu",
      links: [
        "https://www.brookings.edu/blog/order-from-chaos/2020/11/06/the-eus-new-pact-on-migration-and-asylum-is-missing-a-true-foundation/"
      ]
    },
    {
      organization: "The Conversation",
      links: [
        "https://theconversation.com/new-eu-migration-pact-driven-by-political-compromise-not-humanitarianism-146862"
      ]
    },
    {
      organization: "Origins",
      links: [
        "https://origins.osu.edu/article/refugees-or-immigrants-migration-crisis-europe-historical-perspective"
      ]
    },
    {
      organization: "CNN",
      links: [
        "https://www.cnn.com/2017/01/30/politics/trump-immigration-ban-refugees-trnd/index.html"
      ]
    },
    {
      organization: "Washington Post",
      links: [
        "https://www.washingtonpost.com/graphics/world/historical-migrant-crisis/"
      ]
    },
    {
      organization: "Holocaust Museum",
      links: [
        "https://exhibitions.ushmm.org/americans-and-the-holocaust/how-many-refugees-came-to-the-united-states-from-1933-1945"
      ]
    },
    {
      organization: "UNHCR",
      links: ["https://www.unhcr.org/gcr/GCR_English.pdf"]
    }
  ];

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">Migration Policy Resources</h1>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Educational resources to better understand global migration policies and their impact
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          <Quote />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="max-w-4xl mx-auto px-4 py-12"
          >
            <div className="mb-8">
              <h2 className="font-display text-h2 text-secondary mb-4">Resource Links</h2>
              <p className="text-bodyRegular text-textPrimary mb-6">
                Below you'll find a curated list of resources from reputable organizations that provide information, analysis, and historical context about migration policies worldwide. Click on any organization to expand and see available links.
              </p>
              
              {resources.length === 0 ? (
                <div className="bg-white rounded-xl p-8 shadow-md text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-red-500" />
                  </div>
                  <h3 className="font-display text-h3 text-secondary mb-2">No Resources Available</h3>
                  <p className="text-textPrimary">Resources will be added soon. Please check back later.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {resources.map((resource, index) => (
                    <ResourceCard
                      key={index}
                      {...resource}
                      isExpanded={expandedCard === index}
                      onExpand={() =>
                        setExpandedCard(expandedCard === index ? null : index)
                      }
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="font-display text-h3 text-secondary mb-4">Disclaimer</h3>
              <p className="text-textPrimary">
                These resources are provided for educational purposes. Students for Refugees does not endorse all viewpoints expressed in these links but provides them to encourage research and understanding of the complex issues surrounding migration policies and refugee crises.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default MigrationPolicy;